import {
  type ConclusionFilterChoice,
  type EntityRefFilterChoice,
  type EventOpsFilterChoice,
  type FilterChoice,
  type FilterChoiceCategory,
  type FilterChoiceType,
} from '../types'

const COUPLE = 'couple'
const PARENT_CHILD = 'parent-child'

const VITAL_CONCLUSIONS = [
  { value: 'PREFERRED_NAME', conclusionType: 'NAME', single: true, vital: true },
  { value: 'SEX', conclusionType: 'GENDER', single: true, vital: true },
  {
    value: 'BIRTH',
    conclusionType: 'FACT',
    factType: 'Birth',
    constantCaseFactType: 'BIRTH',
    single: true,
    vital: true,
  },
  {
    value: 'CHRISTENING',
    conclusionType: 'FACT',
    factType: 'Christening',
    constantCaseFactType: 'CHRISTENING',

    single: true,
    vital: true,
  },
  {
    value: 'DEATH',
    conclusionType: 'FACT',
    factType: 'Death',
    constantCaseFactType: 'DEATH',
    single: true,
    vital: true,
  },
  {
    value: 'BURIAL',
    conclusionType: 'FACT',
    factType: 'Burial',
    constantCaseFactType: 'BURIAL',
    single: true,
    vital: true,
  },
] satisfies ConclusionFilterChoice[]

const ALTERNATE_NAME_CONCLUSION = {
  value: 'ALTERNATE_NAME',
  conclusionType: 'ALTERNATE_NAME',
} satisfies ConclusionFilterChoice

const EVENT_CONCLUSIONS = [
  {
    value: 'AFFILIATION',
    conclusionType: 'FACT',
    factType: 'Affiliation',
    constantCaseFactType: 'AFFILIATION',
    event: true,
  },
  {
    value: 'BAR_MITZVAH',
    conclusionType: 'FACT',
    factType: 'BarMitzvah',
    constantCaseFactType: 'BAR_MITZVAH',
    event: true,
    single: true,
  },
  {
    value: 'BAS_MITZVAH',
    conclusionType: 'FACT',
    factType: 'BatMitzvah',
    constantCaseFactType: 'BAT_MITZVAH',
    event: true,
    single: true,
  },
  {
    value: 'CREMATION',
    conclusionType: 'FACT',
    factType: 'Cremation',
    constantCaseFactType: 'CREMATION',
    event: true,
    single: true,
  },
  {
    value: 'IMMIGRATION',
    conclusionType: 'FACT',
    factType: 'Immigration',
    constantCaseFactType: 'IMMIGRATION',
    event: true,
  },
  {
    value: 'MILITARY_SERVICE',
    conclusionType: 'FACT',
    factType: 'MilitaryService',
    constantCaseFactType: 'MILITARY_SERVICE',
    event: true,
  },
  {
    value: 'NATURALIZATION',
    conclusionType: 'FACT',
    factType: 'Naturalization',
    constantCaseFactType: 'NATURALIZATION',
    event: true,
  },
  {
    value: 'OCCUPATION',
    conclusionType: 'FACT',
    factType: 'Occupation',
    constantCaseFactType: 'OCCUPATION',
    event: true,
  },
  {
    value: 'RELIGION',
    conclusionType: 'FACT',
    factType: 'Religion',
    constantCaseFactType: 'RELIGION',
    event: true,
  },
  { value: 'RESIDENCE', conclusionType: 'FACT', factType: 'Residence', constantCaseFactType: 'RESIDENCE', event: true },
  {
    value: 'STILLBORN',
    conclusionType: 'FACT',
    factType: 'Stillbirth',
    constantCaseFactType: 'STILLBIRTH', // changelog endpoint expects this
    event: true,
    single: true,
  },
  {
    value: 'NOBILITY_TITLE',
    conclusionType: 'FACT',
    factType: 'TitleOfNobility',
    constantCaseFactType: 'TITLE_OF_NOBILITY', // changelog endpoint expects this
    event: true,
  },
  {
    value: 'OTHER_EVENT',
    conclusionType: 'FACT',
    factType: 'Other',
    constantCaseFactType: 'USER_DEFINED', // changelog endpoint expects this
    event: true,
  },
] satisfies ConclusionFilterChoice[]

const FACT_CONCLUSIONS = [
  { value: 'CASTE', conclusionType: 'FACT', factType: 'Caste', constantCaseFactType: 'CASTE' },
  { value: 'CLAN_NAME', conclusionType: 'FACT', factType: 'Clan', constantCaseFactType: 'CLAN' },
  {
    value: 'LIFE_SKETCH',
    conclusionType: 'FACT',
    factType: 'Life_Sketch', // one-off for the factType casing
    constantCaseFactType: 'LIFE_SKETCH',
  },
  { value: 'NATIONAL_ID', conclusionType: 'FACT', factType: 'NationalId', constantCaseFactType: 'NATIONAL_ID' },
  { value: 'NATIONALITY', conclusionType: 'FACT', factType: 'Nationality', constantCaseFactType: 'NATIONALITY' },
  { value: 'NO_CHILDREN', conclusionType: 'FACT', factType: 'NoChildren', constantCaseFactType: 'NO_CHILDREN' },
  {
    value: 'NO_COUPLE_RELATIONSHIPS',
    conclusionType: 'FACT',
    factType: 'NoCoupleRelationships',
    constantCaseFactType: 'NO_COUPLE_RELATIONSHIPS',
  },
  {
    value: 'PHYSICAL_DESCRIPTION',
    conclusionType: 'FACT',
    factType: 'PhysicalDescription',
    constantCaseFactType: 'PHYSICAL_DESCRIPTION',
  },
  { value: 'ETHNICITY', conclusionType: 'FACT', factType: 'Ethnicity', constantCaseFactType: 'ETHNICITY' },
  { value: 'TRIBE_NAME', conclusionType: 'FACT', factType: 'Tribe', constantCaseFactType: 'TRIBE_NAME' },
  {
    value: 'OTHER_FACT',
    conclusionType: 'FACT',
    factType: 'Other',
    constantCaseFactType: 'USER_DEFINED', // changelog endpoint expects this
    event: true,
  },
] satisfies ConclusionFilterChoice[]

const MEMORY_TYPES = [
  { value: 'PHOTO', entityRefType: 'PHOTO', memory: true },
  { value: 'STORY', entityRefType: 'STORY', memory: true },
  { value: 'AUDIO', entityRefType: 'AUDIO', memory: true },
  { value: 'OBITUARY', entityRefType: 'OBITUARY', memory: true },
  { value: 'DOCUMENT', entityRefType: 'DOCUMENT', memory: true },
] satisfies EntityRefFilterChoice[]

const COUPLE_RELATIONSHIP_CONCLUSIONS = [
  {
    value: 'MARRIAGE',
    conclusionType: 'FACT',
    factType: 'Marriage',
    constantCaseFactType: 'MARRIAGE',
    event: true,
    relationshipType: COUPLE,
  },
  {
    value: 'DIVORCE',
    conclusionType: 'FACT',
    factType: 'Divorce',
    constantCaseFactType: 'DIVORCE',
    event: true,
    relationshipType: COUPLE,
  },
  {
    value: 'ANNULMENT',
    conclusionType: 'FACT',
    factType: 'Annulment',
    constantCaseFactType: 'ANNULMENT',
    event: true,
    relationshipType: COUPLE,
  },
  {
    value: 'COMMON_LAW_MARRIAGE',
    conclusionType: 'FACT',
    factType: 'CommonLawMarriage',
    constantCaseFactType: 'COMMON_LAW_MARRIAGE',
    event: true,
    relationshipType: COUPLE,
  },
  {
    value: 'LIVED_TOGETHER',
    conclusionType: 'FACT',
    factType: 'LivedTogether',
    constantCaseFactType: 'LIVED_TOGETHER',
    event: true,
    relationshipType: COUPLE,
  },
  {
    value: 'COUPLE_NEVER_HAD_CHILDREN',
    conclusionType: 'FACT',
    factType: 'CoupleNeverHadChildren',
    constantCaseFactType: 'COUPLE_NEVER_HAD_CHILDREN',
    relationshipType: COUPLE,
  },
] satisfies ConclusionFilterChoice[]

const PARENT_CHILD_RELATIONSHIP_CONCLUSIONS = [
  {
    value: 'ADOPTIVE_PARENT',
    conclusionType: 'FACT',
    factType: 'AdoptiveParent',
    constantCaseFactType: 'ADOPTIVE_PARENT',
    relationshipType: PARENT_CHILD,
  },
  {
    value: 'BIOLOGICAL_PARENT',
    conclusionType: 'FACT',
    factType: 'BiologicalParent',
    constantCaseFactType: 'BIOLOGICAL_PARENT',
    relationshipType: PARENT_CHILD,
  },
  {
    value: 'FOSTER_PARENT',
    conclusionType: 'FACT',
    factType: 'FosterParent',
    constantCaseFactType: 'FOSTER_PARENT',
    relationshipType: PARENT_CHILD,
  },
  {
    value: 'GUARDIAN_PARENT',
    conclusionType: 'FACT',
    factType: 'GuardianParent',
    constantCaseFactType: 'GUARDIAN_PARENT',
    relationshipType: PARENT_CHILD,
  },
  {
    value: 'STEP_PARENT',
    conclusionType: 'FACT',
    factType: 'StepParent',
    constantCaseFactType: 'STEP_PARENT',
    relationshipType: PARENT_CHILD,
  },
] satisfies ConclusionFilterChoice[]

const ASSOCIATION_RELATIONSHIP_CONCLUSIONS = (
  [
    { type: 'APPRENTICESHIP', translationKey: 'MASTER_TO_APPRENTICE' },
    { type: 'EMPLOYMENT', translationKey: 'EMPLOYER_TO_EMPLOYEE' },
    { type: 'GODPARENTHOOD', translationKey: 'GODPARENT_TO_GODCHILD' },
    { type: 'HOUSEHOLD', translationKey: 'HEAD_OF_HOUSEHOLD_TO_OCCUPANT' },
    { type: 'NEIGHBORHOOD', translationKey: 'NEIGHBOR_TO_NEIGHBOR' },
    { type: 'RELATION', translationKey: 'RELATIVE_TO_RELATIVE' },
    { type: 'EMANCIPATION', translationKey: 'EMANCIPATION' },
    { type: 'ENSLAVEMENT', translationKey: 'ENSLAVEMENT' },
  ] as const
)
  .map(({ type, translationKey }) => {
    return {
      value: type,
      conclusionType: 'FACT',
      factType: type[0] + type.substring(1).toLowerCase(),
      constantCaseFactType: type,
      relationshipType: 'association',
      translationKey,
    } satisfies ConclusionFilterChoice
  })
  .sort((a, b) => {
    if (a.conclusionType > b.conclusionType) return 1
    return -1
  })

export const SOURCES_TYPE = {
  value: 'SOURCES',
  entityRefType: 'SOURCE',
} satisfies EntityRefFilterChoice

export const NOTES_TYPE = {
  value: 'NOTES',
  eventOps: ['NOTE_ADDED', 'NOTE_EDITED', 'NOTE_DELETED'],
} satisfies EventOpsFilterChoice

export const DISCUSSIONS_TYPE = {
  value: 'DISCUSSIONS',
  entityRefType: 'DISCUSSION',
} satisfies EntityRefFilterChoice

export const PERSON_NOT_A_MATCH_TYPE = {
  value: 'PERSON_NOT_A_MATCH',
  entityRefType: 'PERSON_NOT_A_MATCH',
} satisfies EntityRefFilterChoice

export const MERGE_TYPES = [
  {
    value: 'MERGES',
    eventOps: ['PERSON_MERGED', 'PERSON_UNMERGED'],
  },
] satisfies EventOpsFilterChoice[]

export const SPLIT_TYPES = [
  {
    value: 'SPLITS',
    eventOps: ['PERSON_SPLIT'],
  },
] satisfies EventOpsFilterChoice[]

const FIRST_ANCESTOR_TYPE = { value: 'FIRST_ANCESTOR', entityRefType: 'FIRST_ANCESTOR' } satisfies EntityRefFilterChoice

export const FILTER_GROUPS = {
  ALL: [{ value: 'ALL' }],
  VITALS: VITAL_CONCLUSIONS,
  ALTERNATE_NAME: [ALTERNATE_NAME_CONCLUSION],
  EVENTS: EVENT_CONCLUSIONS,
  FACTS: FACT_CONCLUSIONS,
  SOURCES: [SOURCES_TYPE],
  MEMORIES: MEMORY_TYPES,
  MERGES: MERGE_TYPES,
  SPLITS: SPLIT_TYPES,
  DISCUSSIONS: [DISCUSSIONS_TYPE, NOTES_TYPE],
  PERSON_NOT_A_MATCH: [PERSON_NOT_A_MATCH_TYPE],
  COUPLE_RELATIONSHIPS: COUPLE_RELATIONSHIP_CONCLUSIONS,
  PARENT_CHILD_RELATIONSHIPS: PARENT_CHILD_RELATIONSHIP_CONCLUSIONS,
  ASSOCIATION: ASSOCIATION_RELATIONSHIP_CONCLUSIONS,
  FIRST_ANCESTOR: [FIRST_ANCESTOR_TYPE],
} satisfies Record<FilterChoiceCategory, FilterChoice[]>

export const isConclusionFilterChoice = (choice: FilterChoice): choice is ConclusionFilterChoice =>
  'conclusionType' in choice && Boolean(choice satisfies ConclusionFilterChoice)

export const isEventOpsFilterChoice = (choice: FilterChoice): choice is EventOpsFilterChoice =>
  'eventOps' in choice && Boolean(choice satisfies EventOpsFilterChoice)

export const isEntityRefFilterChoice = (choice: FilterChoice): choice is EntityRefFilterChoice =>
  'entityRefType' in choice && Boolean(choice satisfies EntityRefFilterChoice)

export const findFilterInFilterGroups = (filterChoiceType: FilterChoiceType | undefined): FilterChoice | undefined =>
  Object.values(FILTER_GROUPS)
    .flat()
    .find((group) => group.value === filterChoiceType)
